import * as React from "react"
import { Plus } from "lucide-react"
import { MessageEditor, messageBlocksSave } from "@/components/automations/MessageEditor"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type {
  LanguageOptions,
  MessageTranslationDraft,
  SetupTranslation,
} from "@/types/automation"

/**
 * One language version as it stands in the form.
 *
 * `defaultMessage` is the automation's default in this language, on an
 * organization's setup only. When there is one, the version can be reset to it
 * or left out; when there is none, it can only be removed.
 */
export type LanguageDraft = {
  /** Stable across edits, so removing a version never moves focus. */
  id: string
  language: string
  message: string
  defaultMessage: string | null
  leftOut: boolean
}

const PREFERRED_LANGUAGE = "preferred_language"
const OTHER = "__other__"
const LANGUAGE_LIMIT = 40

/** How a member's preferred_language is compared: trimmed, without regard to case. */
export function languageKey(language: string): string {
  return language.replace(/^[ \t\r\n\f\v]+|[ \t\r\n\f\v]+$/g, "").toLowerCase()
}

/** The admin template's defaults, as drafts. */
export function draftsFromDefaults(translations: MessageTranslationDraft[]): LanguageDraft[] {
  return translations.map((translation, index) => ({
    id: `language-${index}`,
    language: translation.language,
    message: translation.message,
    defaultMessage: null,
    leftOut: false,
  }))
}

/**
 * A setup's languages: every default the automation carries, overlaid with the
 * setup's own version or its decision to leave it out, then the setup's own
 * languages the automation has no default for.
 */
export function draftsForSetup(
  defaults: MessageTranslationDraft[],
  own: SetupTranslation[],
): LanguageDraft[] {
  const drafts: LanguageDraft[] = defaults.map((fallback, index) => {
    const mine = own.find((row) => languageKey(row.language) === languageKey(fallback.language))
    return {
      id: `language-${index}`,
      language: mine?.language ?? fallback.language,
      message: mine && !mine.left_out ? (mine.message ?? "") : fallback.message,
      defaultMessage: fallback.message,
      leftOut: mine?.left_out ?? false,
    }
  })

  const defaultKeys = defaults.map((fallback) => languageKey(fallback.language))
  own
    .filter((row) => !row.left_out && !defaultKeys.includes(languageKey(row.language)))
    .forEach((row) =>
      drafts.push({
        id: `language-${drafts.length}`,
        language: row.language,
        message: row.message ?? "",
        defaultMessage: null,
        leftOut: false,
      }),
    )

  return drafts
}

/**
 * What a setup posts: a default used unchanged is not stored at all, a default
 * left out is stored as that decision, and anything else is the setup's own.
 */
export function setupPayload(drafts: LanguageDraft[]): SetupTranslation[] {
  return drafts.flatMap<SetupTranslation>((draft) => {
    if (draft.defaultMessage !== null && draft.leftOut) {
      return [{ language: draft.language, message: null, left_out: true }]
    }
    if (draft.defaultMessage !== null && draft.message === draft.defaultMessage) return []
    return [{ language: draft.language, message: draft.message, left_out: false }]
  })
}

/** What the admin template posts. */
export function defaultsPayload(drafts: LanguageDraft[]): MessageTranslationDraft[] {
  return drafts.map((draft) => ({ language: draft.language, message: draft.message }))
}

/** Whether any version would be refused on save. */
export function languagesBlockSave(drafts: LanguageDraft[], variables: string[]): boolean {
  return drafts.some(
    (draft) =>
      !draft.leftOut &&
      (draft.message.trim() === "" || messageBlocksSave(draft.message, variables)),
  )
}

/**
 * Add Another Language, and a message editor for every language added — the
 * same editor as the English Message, with its Insert buttons, its counter and
 * its checks. The organization's setup passes `renderPreview` to put a preview
 * under each one.
 */
export function LanguageVersions({
  drafts,
  onChange,
  variables,
  languageOptions,
  max,
  errors,
  renderPreview,
  hint,
  readOnly = false,
}: {
  drafts: LanguageDraft[]
  onChange: (drafts: LanguageDraft[]) => void
  variables: string[]
  /** Undefined while the deferred prop is still loading. */
  languageOptions: LanguageOptions | undefined
  max: number
  errors: Record<string, string | undefined>
  renderPreview?: (draft: LanguageDraft) => React.ReactNode
  hint?: string
  /**
   * Read only access: every version and its preview, and none of Add Another
   * Language, Remove, Send again or Don't send.
   */
  readOnly?: boolean
}) {
  const [adding, setAdding] = React.useState(false)
  const [choice, setChoice] = React.useState("")
  const [typed, setTyped] = React.useState("")
  // New drafts number on from those the page rendered with, so ids made on the
  // server and in the browser agree.
  const nextId = React.useRef(drafts.length)

  const usedKeys = drafts.map((draft) => languageKey(draft.language))
  const available = (languageOptions?.languages ?? []).filter(
    (language) => !usedKeys.includes(languageKey(language)),
  )

  const candidate = (choice === OTHER ? typed : choice).replace(/\s+/g, " ").trim()
  const candidateProblem =
    candidate === ""
      ? null
      : languageKey(candidate) === "english"
        ? "The English Message above already covers English."
        : usedKeys.includes(languageKey(candidate))
          ? `${candidate} is already here.`
          : null

  // Only worth saying when the query has been checked and there is something
  // it would affect.
  const queryLacksLanguage =
    variables.length > 0 &&
    !variables.some((variable) => variable.toLowerCase() === PREFERRED_LANGUAGE)

  const update = (id: string, patch: Partial<LanguageDraft>) =>
    onChange(drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)))

  const remove = (id: string) => onChange(drafts.filter((draft) => draft.id !== id))

  const closeAdder = () => {
    setAdding(false)
    setChoice("")
    setTyped("")
  }

  const add = () => {
    if (candidate === "" || candidateProblem) return

    onChange([
      ...drafts,
      {
        id: `language-${nextId.current++}`,
        language: candidate,
        message: "",
        defaultMessage: null,
        leftOut: false,
      },
    ])
    closeAdder()
  }

  return (
    <div className="space-y-6">
      {queryLacksLanguage && (drafts.length > 0 || adding) && (
        <div className="callout callout-signal py-3">
          <p className="text-xs text-ink-body">
            This automation&rsquo;s query doesn&rsquo;t return preferred_language, so every
            member gets the English Message until it does.
          </p>
        </div>
      )}

      {drafts.map((draft, index) => (
        <div key={draft.id} className="space-y-3 border-t border-hairline pt-6">
          {draft.leftOut ? (
            <div className="space-y-2">
              <h3>{draft.language} Message</h3>
              <p className="text-sm text-ink-muted">
                Not sent in {draft.language}. Members who prefer {draft.language} get the
                English Message.
              </p>
            </div>
          ) : (
            <>
              <MessageEditor
                id={`message_language_${index}`}
                label={`${draft.language} Message`}
                value={draft.message}
                variables={variables}
                resetTo={draft.defaultMessage ?? undefined}
                error={errors[`translations.${draft.language}`]}
                hint={hint}
                description={
                  readOnly ? undefined : (
                    <>
                      The Portal doesn&rsquo;t translate. Type the {draft.language} wording
                      yourself
                      {draft.message.trim() === ""
                        ? `, or remove ${draft.language} if it isn’t needed.`
                        : "."}
                    </>
                  )
                }
                readOnly={readOnly}
                onChange={(message) => update(draft.id, { message })}
              />
              {renderPreview?.(draft)}
            </>
          )}

          {!readOnly && (
          <div className="flex flex-wrap gap-2">
            {draft.defaultMessage === null ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(draft.id)}>
                Remove {draft.language}
              </Button>
            ) : draft.leftOut ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => update(draft.id, { leftOut: false })}
              >
                Send in {draft.language} again
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => update(draft.id, { leftOut: true })}
              >
                Don&rsquo;t send in {draft.language}
              </Button>
            )}
          </div>
          )}
        </div>
      ))}

      {errors.translations && (
        <p className="text-xs text-danger-display">{errors.translations}</p>
      )}

      {!readOnly && (
      <div className="border-t border-hairline pt-6">
        {adding ? (
          <div className="space-y-2">
            <label htmlFor="new_language">Language</label>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                id="new_language"
                wrapperClassName="w-full sm:w-60"
                value={choice}
                onChange={(event) => setChoice(event.target.value)}
              >
                <option value="">
                  {languageOptions === undefined ? "Loading languages…" : "Choose a language…"}
                </option>
                {available.map((language) => (
                  <option key={language} value={language}>
                    {language}
                  </option>
                ))}
                <option value={OTHER}>Another language…</option>
              </Select>

              {choice === OTHER && (
                <Input
                  aria-label="Language name"
                  className="w-full sm:w-48"
                  value={typed}
                  maxLength={LANGUAGE_LIMIT}
                  placeholder="Polish"
                  autoFocus
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      add()
                    }
                  }}
                />
              )}

              <Button
                type="button"
                size="sm"
                disabled={candidate === "" || Boolean(candidateProblem)}
                onClick={add}
              >
                Add
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={closeAdder}>
                Cancel
              </Button>
            </div>

            {candidateProblem ? (
              <p className="text-xs text-danger-display">{candidateProblem}</p>
            ) : (
              <p className="text-xs text-ink-muted">{optionsNote(languageOptions)}</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={drafts.length >= max}
              onClick={() => setAdding(true)}
            >
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
              Add Another Language
            </Button>
            <p className="text-xs text-ink-muted">
              {drafts.length >= max
                ? `${max} languages is the most one message can carry.`
                : "Members whose preferred language has its own version get that version. Everyone else — no preference, English, or a language with no version — gets the English Message."}
            </p>
          </div>
        )}
      </div>
      )}
    </div>
  )
}

function optionsNote(options: LanguageOptions | undefined): string {
  if (options === undefined) return "Checking which languages members prefer…"

  switch (options.reason) {
    case "column_missing":
      return "The roster doesn’t record a preferred language yet, so there is nothing to choose from. You can still type one."
    case "unreachable":
      return "The roster couldn’t be reached to list members’ languages. You can still type one."
    case "not_configured":
      return "No roster connection is configured here, so languages can’t be listed. You can still type one."
    default:
      return options.languages.length === 0
        ? "No member has another preferred language on record. You can still type one."
        : "These are the preferred languages on members’ records. Type one under Another language if it isn’t listed."
  }
}
